import { useState, useEffect } from 'react';
import { useMoanaPaye } from '../../contexts/MoanaPayeContext';
import {
  User, FileText, Calendar, Download, TrendingUp,
  Loader2, Briefcase, MapPin, Phone, Mail, ChevronDown, BarChart3,
  Plus, X, Check, Trash2, Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => v != null ? Number(v).toLocaleString('fr-FR') : '-';
const MOIS_NOMS = ['','Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];

const TABS = [
  { id: 'profil', label: 'Mon profil', icon: User },
  { id: 'bulletins', label: 'Mes bulletins', icon: FileText },
  { id: 'conges', label: 'Mes congés', icon: Calendar },
  { id: 'etats', label: 'Recapitulatif annuel', icon: BarChart3 },
];

function Card({ children, className = '' }) {
  return <div className={`bg-[#1a2332] border border-white/10 rounded-xl ${className}`}>{children}</div>;
}

function InfoRow({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-0">
      {Icon && <Icon className="w-4 h-4 text-slate-500 flex-shrink-0" />}
      <span className="text-slate-400 text-xs w-36 flex-shrink-0">{label}</span>
      <span className="text-white text-sm font-medium">{value || '-'}</span>
    </div>
  );
}

function CongeGauge({ label, acquis, pris, color = '#2D5A5A' }) {
  const restant = acquis - pris;
  const pct = acquis > 0 ? Math.min((pris / acquis) * 100, 100) : 0;
  return (
    <div className="bg-white/5 rounded-xl p-4" data-testid={`conge-gauge-${label}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-slate-400 text-xs">{label}</span>
        <span className="text-white text-sm font-bold">{restant} j</span>
      </div>
      <div className="h-2.5 bg-white/10 rounded-full overflow-hidden mb-2">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <div className="flex justify-between text-xs">
        <span className="text-slate-500">Acquis: {acquis} j</span>
        <span style={{ color }}>Pris: {pris} j</span>
      </div>
    </div>
  );
}

export default function EspaceSalariePage() {
  const { mpFetch, token } = useMoanaPaye();
  const [activeTab, setActiveTab] = useState('profil');
  const [tabOpen, setTabOpen] = useState(false);

  return (
    <div data-testid="mp-espace-salarie">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-[#2D5A5A] flex items-center justify-center">
          <User className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">Mon espace</h2>
          <p className="text-slate-500 text-xs">Consultez vos bulletins, conges et informations</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-5">
        <div className="lg:hidden">
          <button onClick={() => setTabOpen(!tabOpen)} className="w-full flex items-center justify-between bg-[#1a2332] border border-white/10 rounded-xl px-4 py-3 text-white text-sm">
            <span className="flex items-center gap-2">
              {(() => { const T = TABS.find(t => t.id === activeTab); return T ? <><T.icon className="w-4 h-4" />{T.label}</> : null; })()}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${tabOpen ? 'rotate-180' : ''}`} />
          </button>
          {tabOpen && (
            <div className="mt-1 bg-[#1a2332] border border-white/10 rounded-xl overflow-hidden">
              {TABS.map(t => (
                <button key={t.id} onClick={() => { setActiveTab(t.id); setTabOpen(false); }}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${activeTab === t.id ? 'bg-[#2D5A5A] text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                  <t.icon className="w-4 h-4" />{t.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="hidden lg:flex gap-1 bg-[#1a2332] border border-white/10 rounded-xl p-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`mp-espace-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === t.id ? 'bg-[#2D5A5A] text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'profil' && <ProfilTab mpFetch={mpFetch} />}
      {activeTab === 'bulletins' && <BulletinsTab mpFetch={mpFetch} token={token} />}
      {activeTab === 'conges' && <CongesTab mpFetch={mpFetch} />}
      {activeTab === 'etats' && <EtatsTab mpFetch={mpFetch} />}
    </div>
  );
}

/* ============ MON PROFIL ============ */
function ProfilTab({ mpFetch }) {
  const [profil, setProfil] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    mpFetch('/api/moana-paye/mon-espace/profil').then(setProfil).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#2D5A5A]" /></div>;
  if (!profil) return <Card className="p-6 text-center"><p className="text-slate-400 text-sm">Profil non disponible</p></Card>;

  return (
    <div data-testid="mp-espace-profil" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="p-5">
        <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
          <User className="w-4 h-4 text-[#2D5A5A]" />Identite
        </h3>
        <InfoRow label="Nom" value={`${profil.nom} ${profil.prenom}`} icon={User} />
        <InfoRow label="Date de naissance" value={profil.date_naissance} />
        <InfoRow label="Nationalite" value={profil.nationalite} />
        <InfoRow label="N. CPS" value={profil.numero_cps} />
        <InfoRow label="Email" value={profil.email} icon={Mail} />
        <InfoRow label="Téléphone" value={profil.telephone} icon={Phone} />
        <InfoRow label="Adresse" value={[profil.adresse_ligne1, profil.ville, profil.ile].filter(Boolean).join(', ')} icon={MapPin} />
      </Card>

      <Card className="p-5">
        <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-[#E8A020]" />Emploi & Contrat
        </h3>
        <InfoRow label="Entreprise" value={profil.entreprise} />
        <InfoRow label="Poste" value={profil.poste_occupe} icon={Briefcase} />
        <InfoRow label="Statut" value={profil.statut} />
        <InfoRow label="Date d'entree" value={profil.date_entree} />
        <InfoRow label="Anciennete depuis" value={profil.date_anciennete || profil.date_entree} />
        <InfoRow label="Type de contrat" value={profil.type_contrat} />
        <InfoRow label="Classification" value={profil.classification_cct} />
        <InfoRow label="Salaire de base" value={profil.salaire_base ? `${fmt(profil.salaire_base)} F CFP` : '-'} icon={TrendingUp} />
      </Card>
    </div>
  );
}

/* ============ MES BULLETINS ============ */
function BulletinsTab({ mpFetch, token }) {
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  useEffect(() => {
    mpFetch('/api/moana-paye/mon-espace/bulletins').then(setBulletins).catch(console.error).finally(() => setLoading(false));
  }, []);

  const downloadPdf = async (bid, titre) => {
    setDownloading(bid);
    try {
      const res = await fetch(`${API}/api/moana-paye/mon-espace/bulletins/${bid}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erreur telechargement');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${titre}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
    setDownloading(null);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#2D5A5A]" /></div>;

  return (
    <div data-testid="mp-espace-bulletins">
      {bulletins.length === 0 ? (
        <Card className="p-10 text-center">
          <FileText className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Aucun bulletin de paie disponible</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {bulletins.map(b => (
            <Card key={b.id} className="p-4 flex items-center justify-between hover:border-[#2D5A5A]/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#2D5A5A]/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#2D5A5A]" />
                </div>
                <div>
                  <div className="text-white text-sm font-medium">{b.mois_label} {b.annee}</div>
                  <div className="text-slate-500 text-xs">{b.titre}</div>
                </div>
              </div>
              <button onClick={() => downloadPdf(b.id, b.titre)} disabled={downloading === b.id}
                 data-testid={`btn-dl-bulletin-${b.id}`}
                 className="inline-flex items-center gap-1.5 bg-[#2D5A5A] hover:bg-[#3a7272] text-white text-xs font-medium px-3 py-2 rounded-lg transition-colors disabled:opacity-50">
                {downloading === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}Telecharger
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ MES CONGES ============ */
function CongesTab({ mpFetch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [typesConge, setTypesConge] = useState([]);
  const [form, setForm] = useState({ type_absence: 'conges_payes', date_debut: '', date_fin: '', observations: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [demandes, setDemandes] = useState([]);

  const loadAll = () => {
    mpFetch('/api/moana-paye/mon-espace/conges').then(setData).catch(console.error).finally(() => setLoading(false));
    mpFetch('/api/moana-paye/mon-espace/mes-demandes').then(setDemandes).catch(() => {});
    mpFetch('/api/moana-paye/mon-espace/types-conge').then(setTypesConge).catch(() => {});
  };
  useEffect(loadAll, []);

  const handleSubmit = async () => {
    setSubmitting(true); setError(''); setResult(null);
    try {
      const res = await mpFetch('/api/moana-paye/mon-espace/demande-conge', {
        method: 'POST', body: JSON.stringify(form)
      });
      setResult(res);
      setForm({ type_absence: 'conges_payes', date_debut: '', date_fin: '', observations: '' });
      setShowForm(false);
      loadAll();
    } catch (e) { setError(e.message); }
    setSubmitting(false);
  };

  const handleCancel = async (aid) => {
    if (!window.confirm('Annuler cette demande ?')) return;
    try {
      await mpFetch(`/api/moana-paye/mon-espace/demande-conge/${aid}`, { method: 'DELETE' });
      loadAll();
    } catch (e) { alert(e.message); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#2D5A5A]" /></div>;
  if (!data) return <Card className="p-6 text-center"><p className="text-slate-400 text-sm">Conges non disponibles</p></Card>;

  const s = data.solde;
  const TYPE_LABELS = {
    conges_payes: 'Conge paye', conges_anciennete: 'Conge anciennete',
    conges_enfant_malade: 'Enfant malade', conges_speciaux: 'Événement familial',
    sans_solde: 'Sans solde', maladie: 'Maladie', maternite: 'Maternite',
    formation: 'Formation', accident_travail: 'Accident du travail', autre: 'Autre'
  };
  const STATUS_COLORS = {
    demande: 'bg-[#E8A020]/15 text-[#E8A020]', validee: 'bg-green-500/15 text-green-400',
    refusee: 'bg-red-500/15 text-red-400', annulee: 'bg-slate-500/15 text-slate-400'
  };
  const STATUS_LABELS = { demande: 'En attente', validee: 'Approuvee', refusee: 'Refusee', annulee: 'Annulee' };

  return (
    <div data-testid="mp-espace-conges">
      {/* Solde + Bouton demande */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-white font-semibold text-sm">Solde de conges {data.annee}</h3>
        <div className="flex items-center gap-2">
          {s && <div className="bg-[#2D5A5A] text-white text-sm font-bold px-4 py-1.5 rounded-lg" data-testid="total-conges">{s.total_restant} j restants</div>}
          <button onClick={() => { setShowForm(true); setError(''); setResult(null); }} data-testid="btn-demande-conge"
            className="inline-flex items-center gap-1.5 bg-[#E8A020] hover:bg-[#d4911a] text-[#1a2332] text-xs font-bold px-4 py-2 rounded-lg transition-colors">
            <Plus className="w-3.5 h-3.5" />Demander un conge
          </button>
        </div>
      </div>

      {/* Gauges */}
      {s && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <CongeGauge label="Conges N" acquis={s.acquis_n} pris={s.pris_n} color="#2D5A5A" />
          <CongeGauge label="Report N-1" acquis={s.acquis_n1} pris={s.pris_n1} color="#E8A020" />
          <CongeGauge label="Anciennete" acquis={s.conges_anciennete} pris={s.pris_anciennete} color="#6366f1" />
          <CongeGauge label="Enfants" acquis={s.conges_enfants} pris={s.pris_enfants} color="#22c55e" />
        </div>
      )}

      {/* Result notification */}
      {result && (
        <div className="mb-4 bg-green-500/10 border border-green-500/30 rounded-lg p-3" data-testid="demande-result">
          <p className="text-green-400 text-sm">{result.message}</p>
        </div>
      )}

      {/* Formulaire de demande */}
      {showForm && (
        <Card className="p-5 mb-5 border-[#E8A020]/30" data-testid="demande-form">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-white font-semibold text-sm flex items-center gap-2"><Plus className="w-4 h-4 text-[#E8A020]" />Nouvelle demande de conge</h4>
            <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          {error && <div className="text-red-400 text-xs mb-3 bg-red-500/10 p-2 rounded-lg" data-testid="demande-error">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-slate-400 text-xs font-medium mb-1 block">Type de conge</label>
              <select value={form.type_absence} onChange={e => setForm({ ...form, type_absence: e.target.value })} data-testid="demande-type"
                className="w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#2D5A5A]">
                {typesConge.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
              </select>
            </div>
            <div />
            <div>
              <label className="text-slate-400 text-xs font-medium mb-1 block">Date de debut</label>
              <input type="date" value={form.date_debut} onChange={e => setForm({ ...form, date_debut: e.target.value })} data-testid="demande-debut"
                className="w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#2D5A5A]" />
            </div>
            <div>
              <label className="text-slate-400 text-xs font-medium mb-1 block">Date de fin</label>
              <input type="date" value={form.date_fin} onChange={e => setForm({ ...form, date_fin: e.target.value })} data-testid="demande-fin"
                className="w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#2D5A5A]" />
            </div>
          </div>
          <div className="mb-4">
            <label className="text-slate-400 text-xs font-medium mb-1 block">Observations (optionnel)</label>
            <textarea value={form.observations} onChange={e => setForm({ ...form, observations: e.target.value })} data-testid="demande-obs"
              className="w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm h-16 focus:outline-none focus:border-[#2D5A5A]"
              placeholder="Motif ou precisions..." />
          </div>
          <button onClick={handleSubmit} disabled={submitting || !form.date_debut || !form.date_fin} data-testid="demande-submit"
            className="inline-flex items-center gap-1.5 bg-[#2D5A5A] hover:bg-[#3a7272] text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors disabled:opacity-50">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Envoyer la demande
          </button>
        </Card>
      )}

      {/* Mes demandes en cours */}
      {demandes.length > 0 && (
        <div className="mb-5">
          <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-[#E8A020]" />Mes demandes</h3>
          <div className="space-y-2">
            {demandes.map(d => (
              <Card key={d.id} className="p-3 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col">
                    <span className="text-white text-xs font-medium">{TYPE_LABELS[d.type_absence] || d.type_absence}</span>
                    <span className="text-slate-500 text-xs">{d.date_debut} au {d.date_fin} — {d.nb_jours_ouvres} j</span>
                    {d.observations && <span className="text-slate-600 text-xs italic">{d.observations}</span>}
                    {d.motif_refus && <span className="text-red-400 text-xs">Motif: {d.motif_refus}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[d.statut]}`}>
                    {STATUS_LABELS[d.statut] || d.statut}
                  </span>
                  {d.statut === 'demande' && (
                    <button onClick={() => handleCancel(d.id)} className="text-red-400 hover:text-red-300" title="Annuler" data-testid={`btn-annuler-${d.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Historique absences */}
      <h3 className="text-white font-semibold text-sm mb-3">Historique des absences</h3>
      {data.absences.length === 0 ? (
        <Card className="p-6 text-center"><p className="text-slate-500 text-xs">Aucune absence enregistree</p></Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]" data-testid="mp-absences-table">
            <thead><tr className="bg-[#2D5A5A]/30 text-left">
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Type</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Du</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Au</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">Jours</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Statut</th>
              <th className="px-3 py-2 text-slate-300 font-medium text-xs">Observations</th>
            </tr></thead>
            <tbody className="divide-y divide-white/5">
              {data.absences.map((a, i) => (
                <tr key={i} className="hover:bg-white/3">
                  <td className="px-3 py-2 text-white text-xs font-medium">{TYPE_LABELS[a.type_absence] || a.type_absence}</td>
                  <td className="px-3 py-2 text-slate-400 text-xs">{a.date_debut}</td>
                  <td className="px-3 py-2 text-slate-400 text-xs">{a.date_fin}</td>
                  <td className="px-3 py-2 text-white text-xs text-right font-medium">{a.nb_jours_ouvres}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[a.statut] || 'bg-slate-500/15 text-slate-400'}`}>
                      {STATUS_LABELS[a.statut] || a.statut}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-xs max-w-[150px] truncate">{a.observations || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

/* ============ RECAPITULATIF ANNUEL ============ */
function EtatsTab({ mpFetch }) {
  const [annee, setAnnee] = useState(2026);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = (a) => {
    setLoading(true);
    mpFetch(`/api/moana-paye/mon-espace/etats/${a}`).then(setData).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { load(annee); }, [annee]);

  return (
    <div data-testid="mp-espace-etats">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-slate-400 text-sm">Annee :</span>
        <input type="number" value={annee} onChange={e => setAnnee(parseInt(e.target.value))}
          className="bg-[#0f1520] border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm w-20 focus:outline-none focus:border-[#2D5A5A]" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#2D5A5A]" /></div>
      ) : !data?.cumuls?.mois?.length ? (
        <Card className="p-10 text-center">
          <BarChart3 className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Aucun bulletin pour {annee}</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {[
              { label: 'Brut cumule', val: data.cumuls.brut, color: '#2D5A5A' },
              { label: 'Cotisations', val: data.cumuls.cot_sal, color: '#6366f1' },
              { label: 'CST', val: data.cumuls.cst, color: '#E8A020' },
              { label: 'Net cumule', val: data.cumuls.net, color: '#22c55e' },
            ].map(c => (
              <Card key={c.label} className="p-4 text-center">
                <div className="text-slate-400 text-xs mb-1">{c.label}</div>
                <div className="text-white text-lg font-bold" style={{ color: c.color }}>{fmt(c.val)} F</div>
              </Card>
            ))}
          </div>

          <Card className="overflow-hidden">
            <table className="w-full text-sm" data-testid="mp-etats-table">
              <thead><tr className="bg-[#2D5A5A]/30 text-left">
                <th className="px-3 py-2 text-slate-300 font-medium text-xs">Mois</th>
                <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">Brut</th>
                <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">Cotisations</th>
                <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">CST</th>
                <th className="px-3 py-2 text-slate-300 font-medium text-xs text-right">Net paye</th>
              </tr></thead>
              <tbody className="divide-y divide-white/5">
                {data.cumuls.mois.map(m => (
                  <tr key={m.mois} className="hover:bg-white/3">
                    <td className="px-3 py-2 text-slate-400 text-xs">{m.label}</td>
                    <td className="px-3 py-2 text-white text-xs text-right">{fmt(m.brut)}</td>
                    <td className="px-3 py-2 text-slate-300 text-xs text-right">{fmt(m.cot_sal)}</td>
                    <td className="px-3 py-2 text-slate-300 text-xs text-right">{fmt(m.cst)}</td>
                    <td className="px-3 py-2 text-[#22c55e] text-xs text-right font-medium">{fmt(m.net)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#2D5A5A]/10 border-t border-white/10">
                  <td className="px-3 py-2 text-[#2D5A5A] font-bold text-xs">CUMULS {annee}</td>
                  <td className="px-3 py-2 text-white font-bold text-xs text-right">{fmt(data.cumuls.brut)}</td>
                  <td className="px-3 py-2 text-slate-300 font-bold text-xs text-right">{fmt(data.cumuls.cot_sal)}</td>
                  <td className="px-3 py-2 text-slate-300 font-bold text-xs text-right">{fmt(data.cumuls.cst)}</td>
                  <td className="px-3 py-2 text-[#22c55e] font-bold text-xs text-right">{fmt(data.cumuls.net)}</td>
                </tr>
              </tfoot>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
